import { useState } from 'react';
import { Box, Button, DataTable, Select, Tab, Tabs } from 'grommet';
import { Add } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { FooterNav, HeaderNav } from '/src/components';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import { Card, CardBody, CardHeader, DataTableHeader, DataTableItem, Text } from '/src/components/common';

const FuseAdminPrograms = observer(() => {
  const [selectedProgram, setSelectedProgram] = useState('CA - Low Carbon Fuel Standard');
  const [activeTab, setActiveTab] = useState(0);

  const programs = [
    'CA - Low Carbon Fuel Standard',
    'OR - Clean Fuels Program',
    'WA - WA CFS',
    'BC - Low Carbon Fuel Standard (BC-LCFS)',
    'CFR - Canadian Clean Fuels Regulation',
  ];

  interface FuelData {
    fuelType: string;
    baseline: string;
    units: string;
    updated: string;
  }

  // Fuel data
  const fuelData: FuelData[] = [
    { fuelType: 'Gasoline', baseline: '105.26', units: 'gCO2e/MJ', updated: 'JAN 15 2025' },
    { fuelType: 'Gasoline', baseline: '105.26', units: 'gCO2e/MJ', updated: 'JAN 15 2025' },
    { fuelType: 'Diesel', baseline: '105.26r', units: 'gCO2e/MJ', updated: 'JAN 15 2025' },
    { fuelType: 'Gasoline', baseline: '105.26', units: 'gCO2e/MJ', updated: 'JAN 15 2025' },
    { fuelType: 'Diesel', baseline: '105.26', units: 'gCO2e/MJ', updated: 'JAN 15 2025' },
  ];

  const years = Array.from({ length: 8 }, (_, i) => 2023 + i);

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false}>
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                Program Configuration{' '}
                <Text as="span" size="xlarge" color="status-critical" weight="normal">
                  Mock Data
                </Text>
              </Text>
            </Box>
            <Box direction="row" justify="between" align="center" margin={{ vertical: 'small' }}>
              <Select
                value={selectedProgram}
                options={programs}
                onChange={({ option }) => setSelectedProgram(option)}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  minWidth: '400px',
                }}
              />
              <Button
                primary
                icon={<Add />}
                label="Add Partner"
                onClick={() => {}}
                style={{
                  borderRadius: '8px',
                  fontWeight: 'normal',
                }}
              />
            </Box>

            <Card background="white" elevation="none" round="xsmall" margin={{ bottom: 'large' }}>
              <CardHeader pad={{ horizontal: 'medium', vertical: 'medium' }}>
                <Text size="large" weight="normal">
                  {selectedProgram}
                </Text>
              </CardHeader>
              <CardBody pad="none">
                <Box
                  background="light-4"
                  pad={{ horizontal: 'medium' }}
                  round="small"
                  margin={{ horizontal: 'medium', vertical: 'small' }}
                >
                  <Tabs activeIndex={activeTab} onActive={setActiveTab}>
                    <Tab title={<Text color={activeTab === 0 ? 'brand' : 'dark-3'}>Baseline CI Scores</Text>} />
                    <Tab title={<Text color={activeTab === 1 ? 'brand' : 'dark-3'}>Grid Carbon Intensity</Text>} />
                    <Tab title={<Text color={activeTab === 2 ? 'brand' : 'dark-3'}>Equipment Details</Text>} />
                  </Tabs>
                </Box>

                {activeTab === 0 && (
                  <Box pad="medium">
                    <Box direction="row" gap="small" margin={{ vertical: 'medium' }}>
                      {years.map((year) => (
                        <Box
                          key={year}
                          pad={{ horizontal: 'small', vertical: 'xsmall' }}
                          border={{ color: 'light-4', size: '1px' }}
                          round="medium"
                          style={{
                            minWidth: '70px',
                            textAlign: 'center',
                          }}
                        >
                          <Text size="small">{year}</Text>
                        </Box>
                      ))}
                    </Box>

                    <DataTable
                      pad={{ horizontal: '1.5rem' }}
                      columns={[
                        {
                          property: 'fuelType',
                          header: <DataTableHeader toUpperCase>Fuel Type</DataTableHeader>,
                          render: (datum: FuelData) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.fuelType}</DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'baseline',
                          header: <DataTableHeader toUpperCase>Baseline CI Score</DataTableHeader>,
                          render: (datum: FuelData) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.baseline}</DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'units',
                          header: <DataTableHeader toUpperCase>Units</DataTableHeader>,
                          render: (datum: FuelData) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.units}</DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'updated',
                          header: <DataTableHeader toUpperCase>Updated</DataTableHeader>,
                          render: (datum: FuelData) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.updated}</DataTableItem>
                            </Box>
                          ),
                        },
                      ]}
                      data={fuelData}
                      background={['light-6', 'white']}
                      border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                    />
                  </Box>
                )}
              </CardBody>
            </Card>
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>
    </Box>
  );
});

export default FuseAdminPrograms;
